/**
 * 游戏核心引擎
 * 管理游戏循环、状态、事件
 */

import type { PlayerState } from './Player';
import { createPlayer, isPlayerDead, canBreakthrough, equipItem, unequipItem, getExpMultiplier, getCombatStoneMultiplier, consumeEscapeBuff, getMaxInventorySize, isCultivationBlocked, recalcStats } from './Player';
import { cultivate, breakthrough, passiveCultivate } from './Cultivation';
import { initiateCombat, executeCombatRound, tryFlee } from './Combat';
import type { CombatState } from './Combat';
import { checkEventTrigger, applyEventChoice } from './EventSystem';
import type { GameEvent as RandomEvent, EventChoice } from '../data/events';
import { calculateIdleReward } from './IdleSystem';
import { saveGame, loadGame, deleteSave, hasSave } from './SaveManager';
import { findRealmLevel } from '../data/realms';
import { REALMS } from '../data/realms';
import type { Equipment } from './EquipmentSystem';
import { ITEMS } from '../data/items';
import { generateSpiritualRoot } from '../data/spiritualRoots';
import { generateOrigin } from '../data/origins';
import { assignIdentity } from '../data/identities';
import { learnTechnique, upgradeTechnique, assignStartingTechnique } from './TechniqueSystem';
import { performAlchemy, tryCompleteRecipe } from './AlchemySystem';
import type { Recipe } from '../data/recipes';
import { RECIPES } from '../data/recipes';
import { performForge } from './ForgeSystem';
import { FORGE_RECIPES } from '../data/forgeRecipes';
import { checkAchievements } from './AchievementSystem';
import type { Zone } from '../data/zones';
import { isSectDisciple, checkMissionProgress, submitItemsForMission, redeemShopItem, refreshMissions, checkMandatoryMission, checkRealmMissions } from './SectSystem';
import { generateBounty, isBountyExpired, bountyTimeLeft } from '../data/bounties';
import { openAcupoint, checkMasteryBuffs, getPlayerMeridians } from './MeridianSystem';
import { getCurrentPrice } from './MarketSystem';
import { enhanceEquipment, generateEquipment } from './EquipmentSystem';
import type { EquipSlot } from '../data/equipment';

export type GameEventType =
  | 'cultivate'
  | 'breakthrough'
  | 'combat_start'
  | 'combat_round'
  | 'combat_end'
  | 'save'
  | 'load'
  | 'delete_save'
  | 'idle_reward'
  | 'tick'
  | 'player_died'
  | 'player_revived'
  | 'equip'
  | 'unequip'
  | 'buy_item'
  | 'use_item'
  | 'discard_item'
  | 'event_trigger'
  | 'event_resolved'
  | 'learn_technique'
  | 'upgrade_technique'
  | 'active_cultivate'
  | 'alchemy'
  | 'forge'
  | 'achievement'
  | 'sect'
  | 'meridian';

export interface GameEvent {
  type: GameEventType;
  message: string;
  data?: any;
}

type EventListener = (event: GameEvent) => void;

export class GameEngine {
  public player: PlayerState;
  /** 是否为本回合新创建的角色（用于 UI 判断是否弹取名框） */
  public isNewPlayer: boolean = false;
  private listeners: EventListener[] = [];
  private tickTimer: number | null = null;
  private autoSaveTimer: number | null = null;
  private lastTickTime: number = 0;
  private tickCount: number = 0;
  public combatState: CombatState | null = null;

  constructor() {
    // 尝试加载存档，否则创建新角色
    const saved = loadGame();
    if (saved) {
      this.player = saved;
      // 兼容旧存档：确保新字段存在
      let needsMigration = false;
      if (!this.player.equipment) {
        (this.player as any).equipment = {};
        needsMigration = true;
      }
      if (!this.player.spiritualRoot) {
        (this.player as any).spiritualRoot = generateSpiritualRoot();
        needsMigration = true;
      }
      if (!this.player.originId) {
        const origin = generateOrigin();
        (this.player as any).originId = origin.id;
        needsMigration = true;
      }
      if (!this.player.identityId) {
        const root = this.player.spiritualRoot;
        const origin = generateOrigin(); // 旧存档无法还原出身，随机一个
        const identity = assignIdentity(root.grade, origin);
        (this.player as any).identityId = identity.id;
        (this.player as any).originId = origin.id;
        needsMigration = true;
      }
      if (this.player.escapeBuffRemaining === undefined) {
        (this.player as any).escapeBuffRemaining = 0;
        needsMigration = true;
      }
      if (!this.player.techniqueId) {
        (this.player as any).techniqueId = assignStartingTechnique(this.player.identityId ?? 'rogue');
        (this.player as any).techniqueLayer = 1;
        needsMigration = true;
      }
      // 旧装备槽迁移：storage→waist, armor→body, accessory→ring1
      const slotMap: Record<string, string> = { storage: 'waist', armor: 'body', accessory: 'ring1' };
      for (const [oldSlot, newSlot] of Object.entries(slotMap)) {
        if ((this.player.equipment as any)[oldSlot]) {
          const oldEquip = (this.player.equipment as any)[oldSlot];
          // 补上缺失字段
          if (oldEquip.enhanceLevel === undefined) oldEquip.enhanceLevel = 0;
          if (oldEquip.quality === undefined) oldEquip.quality = 'common';
          if (oldEquip.rollPct === undefined) oldEquip.rollPct = 50;
          if (oldEquip.isPerfect === undefined) oldEquip.isPerfect = false;
          if (oldEquip.slot === undefined) oldEquip.slot = newSlot;
          (this.player.equipment as any)[newSlot] = oldEquip;
          delete (this.player.equipment as any)[oldSlot];
          needsMigration = true;
        }
      }
      // 旧背包中的装备转换为新Equipment对象
      for (let i = this.player.inventory.length - 1; i >= 0; i--) {
        const inv = this.player.inventory[i];
        if (inv && inv.type === 'equipment') {
          const newEquip = generateEquipment(inv.id);
          if (newEquip) {
            this.player.inventory[i] = newEquip;
            needsMigration = true;
          }
        }
      }
      // 确保有腰带
      if (!this.player.equipment['waist']) {
        const belt = generateEquipment('cloth_belt');
        if (belt) { this.player.equipment['waist'] = belt; needsMigration = true; }
      }
      // 修复已迁移但缺少新字段的装备，清洗NaN
      for (const [slot, eq] of Object.entries(this.player.equipment)) {
        if (!eq) continue;
        const e = eq as any;
        if (e.enhanceLevel === undefined || isNaN(e.enhanceLevel)) e.enhanceLevel = 0;
        if (!e.quality) e.quality = 'common';
        if (e.rollPct === undefined || isNaN(e.rollPct)) e.rollPct = 50;
        if (e.isPerfect === undefined) e.isPerfect = false;
        if (typeof e.attackBonus !== 'number' || isNaN(e.attackBonus)) e.attackBonus = 0;
        if (typeof e.defenseBonus !== 'number' || isNaN(e.defenseBonus)) e.defenseBonus = 0;
        if (typeof e.hpBonus !== 'number' || isNaN(e.hpBonus)) e.hpBonus = 0;
        if (typeof e.mpBonus !== 'number' || isNaN(e.mpBonus)) e.mpBonus = 0;
        needsMigration = true;
      }
      // 修复 NaN 属性（上次装备系统bug污染）
      if (isNaN(this.player.hp) || isNaN(this.player.maxHp) || isNaN(this.player.attack) || isNaN(this.player.defense)) {
        const realmLevel = findRealmLevel(this.player.realmKey);
        const mult = realmLevel?.statMultiplier ?? 1;
        this.player.maxHp = Math.floor(100 * mult);
        this.player.hp = this.player.maxHp;
        this.player.maxMp = Math.floor(50 * mult);
        this.player.mp = this.player.maxMp;
        this.player.attack = Math.floor(10 * mult);
        this.player.defense = Math.floor(5 * mult);
        this.player.spiritStones = Math.max(0, this.player.spiritStones || 0);
        needsMigration = true;
      }
      if (this.player.alchemyExp === undefined && (this.player as any).alchemySkill !== undefined) {
        (this.player as any).alchemyExp = ((this.player as any).alchemySkill ?? 0) * 5;
        needsMigration = true;
      }
      if (this.player.alchemyExp === undefined) {
        (this.player as any).alchemyExp = 0;
        needsMigration = true;
      }
      if (this.player.forgeExp === undefined) {
        (this.player as any).forgeExp = 0;
        needsMigration = true;
      }
      if (!this.player.unlockedAchievements) {
        (this.player as any).unlockedAchievements = [];
        needsMigration = true;
      }
      if (this.player.sectContribution === undefined) {
        (this.player as any).sectContribution = 0;
        (this.player as any).sectMissions = [];
        (this.player as any).mandatoryMission = null;
        (this.player as any).mandatoryMissionDoneAt = 0;
        needsMigration = true;
      }
      if (this.player.mandatoryMissionDoneAt === undefined) {
        (this.player as any).mandatoryMissionDoneAt = 0;
        needsMigration = true;
      }
      if (!this.player.meridians) {
        (this.player as any).meridians = {};
        needsMigration = true;
      }
      if (needsMigration) {
        recalcStats(this.player);
        saveGame(this.player);
      }
      // 门派弟子分配强制任务（CD内清除残留，否则分配新任务）
      if (isSectDisciple(this.player)) {
        const msg = checkMandatoryMission(this.player);
        if (msg) this.emit({ type: 'sect', message: msg });
      }
      // 加载后检查状态类成就
      this.checkAndNotifyAchievements();
      // 计算离线收益（强制任务未完成则冻结）
      if (!isCultivationBlocked(this.player)) {
        const idleReward = calculateIdleReward(this.player, Date.now());
        if (idleReward.hasReward) {
          this.emit({ type: 'idle_reward', message: idleReward.message, data: idleReward });
        }
      }
      // 离线归来可能有奇遇
      const idleEvent = checkEventTrigger('idle', this.player);
      if (idleEvent) {
        this.pendingEvent = idleEvent.event;
        this.emit({ type: 'event_trigger', message: '', data: this.pendingEvent });
      }
    } else {
      this.player = createPlayer('无名修士');
      this.isNewPlayer = true;
      // 新角色立即存档，防止刷新丢失灵根
      saveGame(this.player);
    }
  }

  /** 注册事件监听 */
  on(listener: EventListener): () => void {
    this.listeners.push(listener);
    return () => {
      this.listeners = this.listeners.filter((l) => l !== listener);
    };
  }

  /** 触发事件 */
  private emit(event: GameEvent): void {
    for (const listener of this.listeners) {
      try {
        listener(event);
      } catch (e) {
        console.error('事件处理错误:', e);
      }
    }
  }

  /** 开始游戏循环 */
  start(): void {
    this.lastTickTime = Date.now();

    // 每秒 tick
    this.tickTimer = window.setInterval(() => {
      this.tick();
    }, 1000);

    // 每 30 秒自动存档
    this.autoSaveTimer = window.setInterval(() => {
      this.autoSave();
    }, 30000);

    this.emit({ type: 'load', message: '游戏已就绪' });
  }

  /** 停止游戏循环 */
  stop(): void {
    if (this.tickTimer !== null) {
      clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
    if (this.autoSaveTimer !== null) {
      clearInterval(this.autoSaveTimer);
      this.autoSaveTimer = null;
    }
  }

  /** 每秒逻辑更新 */
  private tick(): void {
    const now = Date.now();

    // 被动修炼（在线挂机）——门派强制任务未完成则冻结
    if (this.player.hp > 0 && !isCultivationBlocked(this.player)) {
      const realmLevel = findRealmLevel(this.player.realmKey);
      if (realmLevel) {
        let expMult = getExpMultiplier(this.player);
        // 灵脉加成
        if (this.player.territory) expMult *= (1 + (this.player.territory.veinBonus ?? 0));
        this.player.cultivationExp += realmLevel.passiveExpPerSec * expMult;
      }

      // 灵脉守卫：每30秒5%概率被袭击
      if (this.player.territory && !this.combatState && Math.random() < 0.0017) {
        this.triggerTerritoryDefense();
      }

      // 悬赏过期自动刷新
      if (this.player.bounty && isBountyExpired(this.player.bounty)) {
        this.player.bounty = null;
        this.emit({ type: 'sect', message: '🏹 悬赏已过期，新悬赏即将发布...' });
      }
      if (!this.player.bounty) {
        this.player.bounty = generateBounty(this.player);
        if (this.player.bounty) this.emit({ type: 'sect', message: `🏹 悬赏令：${this.player.bounty.enemyName} 出没于某区域，击杀可获丰厚奖励！` });
      }
      // 缓慢恢复 MP
      if (this.player.mp < this.player.maxMp) {
        this.player.mp = Math.min(this.player.maxMp, this.player.mp + 1);
      }
      // 缓慢恢复 HP（每5秒恢复1点）
      if (this.player.hp < this.player.maxHp) {
        this.tickCount++;
        if (this.tickCount >= 5) {
          this.player.hp = Math.min(this.player.maxHp, this.player.hp + 1);
          this.tickCount = 0;
        }
      }
    }

    // 极低概率在线奇遇
    if (!this.pendingEvent && !this.combatState) {
      const tickEvent = checkEventTrigger('tick_rare', this.player);
      if (tickEvent) {
        this.pendingEvent = tickEvent.event;
        this.emit({ type: 'event_trigger', message: '', data: this.pendingEvent });
      }
    }

    this.emit({ type: 'tick', message: '', data: { time: now } });
  }

  /** 自动存档 */
  private autoSave(): void {
    if (saveGame(this.player)) {
      this.emit({ type: 'save', message: '📀 自动存档完成' });
    }
  }

  // ========== 玩家操作 ==========

  /** 修炼 */
  doCultivate(): void {
    if (isPlayerDead(this.player)) {
      this.emit({ type: 'cultivate', message: '你已阵亡，请先复活！' });
      return;
    }
    const result = cultivate(this.player);
    this.emit({ type: 'cultivate', message: result.message, data: result });

    // 修炼时有概率触发奇遇
    const eventResult = checkEventTrigger('cultivate', this.player);
    if (eventResult) {
      this.pendingEvent = eventResult.event;
      this.emit({ type: 'event_trigger', message: '', data: this.pendingEvent });
    }
  }

  /** 尝试突破 */
  doBreakthrough(): void {
    if (isPlayerDead(this.player)) {
      this.emit({ type: 'breakthrough', message: '你已阵亡，请先复活！' });
      return;
    }
    if (!canBreakthrough(this.player)) {
      this.emit({ type: 'breakthrough', message: '修炼经验不足，还无法突破' });
      return;
    }
    const result = breakthrough(this.player);
    this.emit({ type: 'breakthrough', message: result.message, data: result });

    if (result.breakthrough) {
      consumeEscapeBuff(this.player);
      // 检查修炼类宗门任务
      const realmMsgs = checkRealmMissions(this.player);
      for (const msg of realmMsgs) this.emit({ type: 'sect', message: msg });
    }
    saveGame(this.player);
    this.checkAndNotifyAchievements();
  }

  /** 开始战斗（指定区域） */
  doCombat(zone?: Zone): void {
    if (isPlayerDead(this.player)) {
      this.emit({ type: 'combat_start', message: '你已阵亡，请先复活！' });
      return;
    }
    this.combatState = initiateCombat(this.player, zone);
    const zoneName = zone ? `【${zone.icon}${zone.name}】` : '';
    this.emit({ type: 'combat_start', message: `⚔️ ${zoneName}遭遇了 ${this.combatState.enemy.name}！`, data: this.combatState });
  }

  /** 战斗：攻击 */
  doCombatAttack(): void {
    if (!this.combatState || this.combatState.isOver) return;
    executeCombatRound(this.combatState);
    this.emit({ type: 'combat_round', message: '', data: this.combatState });

    if (this.combatState.isOver) {
      this.finishCombat();
    }
  }

  /** 战斗：逃跑 */
  doCombatFlee(): void {
    if (!this.combatState || this.combatState.isOver) return;
    const result = tryFlee(this.combatState);
    this.emit({ type: 'combat_round', message: result.message, data: this.combatState });

    if (this.combatState.isOver) {
      if (!this.combatState.playerWon && this.player.hp <= 0) {
        deleteSave();
        this.emit({ type: 'player_died', message: '💀 逃跑失败，被击杀了...修仙界没有重来的机会。' });
        this.combatState = null;
        setTimeout(() => {
          const newPlayer = createPlayer('无名修士');
          saveGame(newPlayer);
          location.reload();
        }, 3000);
        return;
      }
      this.combatState = null;
      this.emit({ type: 'combat_end', message: result.success ? '成功逃脱' : '逃跑失败', data: null });
    }
  }

  /** 战斗中使用物品 */
  doCombatUseItem(itemId: string): void {
    if (!this.combatState || this.combatState.isOver) return;
    // 复用 doUseItem 的效果
    this.doUseItem(itemId);
    this.emit({ type: 'combat_round', message: '使用了丹药', data: this.combatState });
  }

  /** 结束战斗 */
  private finishCombat(): void {
    if (!this.combatState) return;
    const state = this.combatState;
    const won = state.playerWon;

    if (won) {
      // 检查战斗后奇遇
      const eventResult = checkEventTrigger('combat_end', this.player);
      if (eventResult) {
        this.pendingEvent = eventResult.event;
      }
    }

    if (!won && this.player.hp <= 0) {
      // 死亡清档
      deleteSave();
      this.emit({ type: 'player_died', message: '💀 你被击杀了。修仙界残酷无情，没有重来的机会...' });
      this.combatState = null;
      setTimeout(() => {
        const newPlayer = createPlayer('无名修士');
        saveGame(newPlayer);
        location.reload();
      }, 3000);
      return;
    }

    this.emit({ type: 'combat_end', message: won ? '战斗胜利！' : '战斗失败...', data: state });
    this.combatState = null;
    if (won) {
      this.notifyKill();
      this.checkBountyCompletion(state.zone?.id, state.enemy.name);
    }
    this.checkAndNotifyAchievements();
    saveGame(this.player);

    // 触发奇遇事件
    if (this.pendingEvent) {
      this.emit({ type: 'event_trigger', message: '', data: this.pendingEvent });
    }
  }

  // ========== 炼丹操作 ==========

  doAlchemy(recipeId: string): void {
    const recipe = RECIPES.find((r: Recipe) => r.id === recipeId);
    if (!recipe) {
      this.emit({ type: 'alchemy', message: '丹方不存在' });
      return;
    }
    const result = performAlchemy(this.player, recipe);
    this.emit({ type: 'alchemy', message: result.message, data: result });
    this.checkAndNotifyAchievements();
  }

  doCompleteRecipe(recipeId: string, extraItemId: string): void {
    const result = tryCompleteRecipe(this.player, recipeId, extraItemId);
    this.emit({ type: 'alchemy', message: result.message, data: result });
  }

  doForge(recipeId: string): void {
    const recipe = FORGE_RECIPES.find((r) => r.id === recipeId);
    if (!recipe) { this.emit({ type: 'forge', message: '配方不存在' }); return; }
    const result = performForge(this.player, recipe);
    this.emit({ type: 'forge', message: result.message, data: result });
    this.checkAndNotifyAchievements();
  }

  // ========== 门派操作 ==========

  doSectSubmit(): void {
    if (!isSectDisciple(this.player)) { this.emit({ type: 'sect', message: '只有门派弟子才能使用宗门功能' }); return; }
    // 顺便检查修炼任务
    const realmMsgs = checkRealmMissions(this.player);
    for (const msg of realmMsgs) this.emit({ type: 'sect', message: msg });
    const mandatoryMsg = checkMandatoryMission(this.player);
    if (mandatoryMsg) {
      this.emit({ type: 'sect', message: mandatoryMsg });
      if (mandatoryMsg.startsWith('✅')) saveGame(this.player);
      this.checkAndNotifyAchievements();
      return;
    }
    const msgs = submitItemsForMission(this.player);
    if (msgs.length === 0) { this.emit({ type: 'sect', message: '没有可提交的任务物品' }); return; }
    for (const msg of msgs) this.emit({ type: 'sect', message: msg });
    saveGame(this.player);
    this.checkAndNotifyAchievements();
  }

  doSectRefresh(): void {
    if (!isSectDisciple(this.player)) { this.emit({ type: 'sect', message: '只有门派弟子才能使用宗门功能' }); return; }
    refreshMissions(this.player);
    this.emit({ type: 'sect', message: '🔄 任务已刷新' });
  }

  doSectRedeem(shopItemId: string): void {
    if (!isSectDisciple(this.player)) { this.emit({ type: 'sect', message: '只有门派弟子才能兑换' }); return; }
    const result = redeemShopItem(this.player, shopItemId);
    this.emit({ type: 'sect', message: result.message });
  }

  notifyKill(): void {
    if (!isSectDisciple(this.player)) return;
    const msgs = checkMissionProgress(this.player, 'kill_enemies', 1);
    for (const msg of msgs) this.emit({ type: 'sect', message: msg });
  }

  // ========== 经脉操作 ==========

  doOpenMeridian(meridianId: string): void {
    const result = openAcupoint(this.player, meridianId);
    this.emit({ type: 'meridian', message: result.message, data: result });
    if (result.success) {
      const masteryMsgs = checkMasteryBuffs(this.player);
      for (const msg of masteryMsgs) this.emit({ type: 'meridian', message: msg });
    }
    this.checkAndNotifyAchievements();
    saveGame(this.player);
  }

  // ========== 功法操作 ==========

  /** 学习/切换功法 */
  doLearnTechnique(techId: string): void {
    const result = learnTechnique(this.player, techId);
    this.emit({ type: 'learn_technique', message: result.message });
  }

  /** 升级功法层数 */
  doUpgradeTechnique(): void {
    const result = upgradeTechnique(this.player);
    this.emit({ type: 'upgrade_technique', message: result.message });
  }

  /** 主动运功（小游戏完成后调用） */
  doActiveCultivate(efficiency: number): void {
    if (isPlayerDead(this.player)) return;
    const mpCost = 20;
    if (this.player.mp < mpCost) {
      this.emit({ type: 'active_cultivate', message: '灵力不足！需要 20 点灵力' });
      return;
    }
    this.player.mp -= mpCost;
    // 效率是打坐速度的倍数（3~8倍），由小游戏结果决定
    const realmLevel = findRealmLevel(this.player.realmKey);
    if (realmLevel) {
      const expMult = getExpMultiplier(this.player);
      const expGained = Math.floor(realmLevel.passiveExpPerSec * 10 * expMult * efficiency);
      this.player.cultivationExp += expGained;
      this.emit({ type: 'active_cultivate', message: `⚡ 运功完毕！获得 ${expGained} 点修炼经验（消耗 ${mpCost} 灵力）` });
    }
  }

  /** 探索灵脉（主动操作） */
  doExploreVein(): void {
    if (this.player.territory) {
      this.emit({ type: 'sect', message: `你已经占领了「${this.player.territory.veinName}」，先撤离才能探索新灵脉。` });
      return;
    }
    const chance = 0.3;
    if (Math.random() < chance) {
      const bonus = 0.3 + Math.random() * 0.4; // 30%~70%加成
      const names = ['微型灵脉', '下品灵脉', '中品灵脉', '上品灵脉'];
      const idx = bonus < 0.4 ? 0 : bonus < 0.5 ? 1 : bonus < 0.6 ? 2 : 3;
      this.player.territory = { veinBonus: Math.round(bonus * 100) / 100, veinName: names[idx] };
      this.emit({ type: 'sect', message: `⛏️ 发现了${names[idx]}！修炼速度+${Math.round(bonus * 100)}%。小心其他修士来抢！` });
    } else {
      this.emit({ type: 'sect', message: '四处探索了一番，没有发现灵脉。' });
    }
  }

  /** 撤离灵脉 */
  doLeaveVein(): void {
    if (!this.player.territory) {
      this.emit({ type: 'sect', message: '你当前没有占领灵脉。' });
      return;
    }
    this.emit({ type: 'sect', message: `撤离了「${this.player.territory.veinName}」，修炼加成消失。` });
    this.player.territory = null;
  }

  /** 灵脉守卫战斗 */
  private triggerTerritoryDefense(): void {
    if (!this.player.territory || this.combatState) return;
    const vein = this.player.territory;
    const realmLevel = findRealmLevel(this.player.realmKey);
    const realmIdx = REALMS.findIndex((r) => r.levels.some((l) => l.key === this.player.realmKey));
    const scale = 1 + realmIdx * 0.5;

    const enemy = {
      name: '夺脉修士',
      hp: Math.floor(150 * scale),
      attack: Math.floor(15 * scale),
      defense: Math.floor(8 * scale),
      expReward: Math.floor(100 * scale),
      stoneReward: Math.floor(80 * scale),
      minRealmIndex: realmIdx,
      drops: [{ itemId: 'spirit_herb', itemName: '灵草', chance: 0.5, countMin: 1, countMax: 2 }],
    };

    this.emit({ type: 'sect', message: `⚠️ 有人想抢你的「${vein.veinName}」！迎战！` });
    this.combatState = {
      player: this.player,
      enemy,
      enemyMaxHp: enemy.hp,
      isOver: false,
      playerWon: false,
      playerFled: false,
      log: [`⚔️ 守卫灵脉：${enemy.name}来袭！`],
      turn: 0,
    };
    this.emit({ type: 'combat_start', message: `⚔️ 灵脉守卫战！`, data: this.combatState });
  }

  /** 悬赏完成检查 */
  private checkBountyCompletion(zoneId: string | undefined, enemyName: string): void {
    const bounty = this.player.bounty;
    if (!bounty || isBountyExpired(bounty)) return;
    if (bounty.zoneId === zoneId && bounty.enemyName === enemyName) {
      this.player.spiritStones += bounty.stoneReward;
      this.player.cultivationExp += bounty.expReward;
      for (let i = 0; i < bounty.herbReward; i++) {
        const h = ITEMS.find((it) => it.id === 'spirit_herb');
        if (h) this.player.inventory.push(JSON.parse(JSON.stringify(h)));
      }
      for (let i = 0; i < bounty.coreReward; i++) {
        const c = ITEMS.find((it) => it.id === 'monster_core');
        if (c) this.player.inventory.push(JSON.parse(JSON.stringify(c)));
      }
      this.emit({ type: 'sect', message: `🏆 悬赏完成！击败${bounty.enemyName}，获得 ${bounty.stoneReward}灵石 + ${bounty.expReward}经验！` });
      this.player.bounty = null;
    }
  }

  /** 检查并通知成就 */
  private checkAndNotifyAchievements(): void {
    const newly = checkAchievements(this.player);
    for (const ach of newly) {
      this.emit({ type: 'achievement', message: `🏆 成就解锁：${ach.icon} ${ach.name} — ${ach.desc}`, data: ach });
    }
  }

  /** 待处理的奇遇事件 */
  private pendingEvent: RandomEvent | null = null;

  /** 处理奇遇事件选择 */
  resolveEvent(choice: EventChoice): void {
    if (!this.pendingEvent) return;
    const messages = applyEventChoice(this.player, choice);
    for (const msg of messages) {
      this.emit({ type: 'event_resolved', message: msg });
    }
    this.pendingEvent = null;
  }

  /** 取消事件（关闭弹窗） */
  dismissEvent(): void {
    this.pendingEvent = null;
  }

  getPendingEvent(): RandomEvent | null {
    return this.pendingEvent;
  }

  /** 复活玩家 */
  revivePlayer(): void {
    this.player.hp = Math.floor(this.player.maxHp * 0.5);
    this.player.mp = Math.floor(this.player.maxMp * 0.3);
    this.emit({ type: 'player_revived', message: '你已复活，继续修仙之路！' });
  }

  // ========== 装备操作 ==========

  /** 装备物品 */
  doEquip(itemId: string): void {
    const item = this.player.inventory.find((i) => i.slot !== undefined && i.id === itemId);
    if (!item) {
      this.emit({ type: 'equip', message: '物品不存在或不是装备' });
      return;
    }
    const slot = item.slot as EquipSlot;
    const oldEquip = this.player.equipment[slot];
    const ok = equipItem(this.player, item);
    if (ok) {
      const msg = oldEquip
        ? `装备了 ${item.name}（替换了 ${oldEquip.name}）`
        : `装备了 ${item.name}`;
      this.emit({ type: 'equip', message: msg, data: { item, oldEquip } });
    }
  }

  /** 卸下装备 */
  doUnequip(slot: EquipSlot): void {
    const item = unequipItem(this.player, slot);
    if (item) {
      this.emit({ type: 'unequip', message: `卸下了 ${item.name}`, data: { slot } });
    }
  }

  /** 强化装备 */
  doEnhance(slot: EquipSlot): void {
    const result = enhanceEquipment(this.player, slot);
    this.emit({ type: 'equip', message: result.message, data: result });
    if (result.ok || result.destroyed) saveGame(this.player);
  }

  /** 购买物品（支持批量） */
  doBuyItem(itemId: string, quantity: number = 1): void {
    const template = ITEMS.find((i) => i.id === itemId);
    if (!template) {
      this.emit({ type: 'buy_item', message: '商店中没有此物品' });
      return;
    }
    const unitPrice = getCurrentPrice(template.price);
    const totalCost = unitPrice * quantity;
    if (this.player.spiritStones < totalCost) {
      this.emit({ type: 'buy_item', message: `灵石不足！${quantity}个 ${template.name} 需要 ${totalCost} 灵石，当前只有 ${this.player.spiritStones}` });
      return;
    }
    const maxSize = getMaxInventorySize(this.player);
    const space = maxSize - this.player.inventory.length;
    if (space < quantity) {
      this.emit({ type: 'buy_item', message: `背包空间不足！需要 ${quantity} 格，只剩 ${space} 格` });
      return;
    }
    this.player.spiritStones -= totalCost;
    for (let i = 0; i < quantity; i++) {
      this.player.inventory.push(JSON.parse(JSON.stringify(template)));
    }
    this.emit({ type: 'buy_item', message: `购买了 ${template.name}×${quantity}，花费 ${totalCost} 灵石`, data: { item: template, quantity } });
  }

  /** 出售物品 */
  doSellItem(itemId: string, quantity: number = 1): void {
    const template = ITEMS.find((i) => i.id === itemId);
    if (!template) { this.emit({ type: 'buy_item', message: '物品不存在' }); return; }
    const sellPrice = Math.floor(getCurrentPrice(template.price) * 0.5);
    let sold = 0;
    for (let i = 0; i < quantity; i++) {
      const idx = this.player.inventory.findIndex((inv) => inv.id === itemId);
      if (idx === -1) break;
      this.player.inventory.splice(idx, 1);
      sold++;
    }
    if (sold === 0) { this.emit({ type: 'buy_item', message: '没有可出售的物品' }); return; }
    const totalGain = sellPrice * sold;
    this.player.spiritStones += totalGain;
    this.emit({ type: 'buy_item', message: `出售了 ${template.name}×${sold}，获得 ${totalGain} 灵石` });
  }

  /** 使用物品 */
  doUseItem(itemId: string): void {
    const idx = this.player.inventory.findIndex((i) => i.id === itemId);
    if (idx === -1) {
      this.emit({ type: 'use_item', message: '背包中没有此物品' });
      return;
    }

    const item = this.player.inventory[idx];
    if (item.type !== 'consumable') {
      this.emit({ type: 'use_item', message: `${item.name} 不能直接使用` });
      return;
    }

    // 应用效果
    let msg = '';
    if (item.hpRestore && this.player.hp < this.player.maxHp) {
      const healed = Math.min(item.hpRestore, this.player.maxHp - this.player.hp);
      this.player.hp += healed;
      msg += `恢复了 ${healed} 点生命`;
    }
    if (item.mpRestore && this.player.mp < this.player.maxMp) {
      const restored = Math.min(item.mpRestore, this.player.maxMp - this.player.mp);
      this.player.mp += restored;
      msg += (msg ? '，' : '') + `恢复了 ${restored} 点灵力`;
    }
    if (item.expBonus) {
      this.player.cultivationExp += item.expBonus;
      msg += (msg ? '，' : '') + `获得了 ${item.expBonus} 点修炼经验`;
    }

    if (!msg) {
      msg = '你目前不需要使用此丹药';
      this.emit({ type: 'use_item', message: msg });
      return;
    }

    // 消耗物品
    this.player.inventory.splice(idx, 1);
    this.emit({ type: 'use_item', message: `使用了 ${item.name}：${msg}`, data: { item } });
  }

  /** 丢弃物品 */
  doDiscardItem(itemId: string): void {
    const idx = this.player.inventory.findIndex((i) => i.id === itemId);
    if (idx === -1) {
      this.emit({ type: 'discard_item', message: '物品不存在' });
      return;
    }
    const item = this.player.inventory[idx];
    this.player.inventory.splice(idx, 1);
    this.emit({ type: 'discard_item', message: `丢弃了 ${item.name}` });
  }

  // ========== 存档操作 ==========

  /** 手动存档 */
  doSave(): boolean {
    const ok = saveGame(this.player);
    this.emit({
      type: 'save',
      message: ok ? '📀 存档成功！' : '❌ 存档失败！',
    });
    return ok;
  }

  /** 读档 */
  doLoad(): boolean {
    const saved = loadGame();
    if (!saved) {
      this.emit({ type: 'load', message: '没有找到存档' });
      return false;
    }
    this.player = saved;
    this.emit({ type: 'load', message: '📂 读档成功！' });
    return true;
  }

  /** 删档 */
  doDeleteSave(): boolean {
    const ok = deleteSave();
    this.emit({
      type: 'delete_save',
      message: ok ? '🗑️ 存档已删除' : '❌ 删档失败',
    });
    if (ok) {
      this.player = createPlayer('无名修士');
    }
    return ok;
  }

  /** 检查是否有存档 */
  hasSaveData(): boolean {
    return hasSave();
  }
}
